import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import Moment from "react-moment";
import PaymentService from "../../app/data/payment/paymentService";
import { PaymentStatusResponse } from "../../app/data/payment/PaymentSession";
import Amount from "../../ui-components/molecules/amount/amount";
import Loading from "../../ui-components/loading/loading";
import XGSIcon from "../../ui-components/icon/xgsIcon";
import XGSIcons from "../../ui-components/icon/xgsIcons";
import { Routes } from "../../app/route/RoutesConfig";
import { copyTextToClipboard } from "../../hooks/utils";
import { PaymentSummary } from "../payments/paymentAmountBreakdown";
import { formatProbillDisplay } from "../shipments/labels-receipts/types";
import PublicPayPortalPromo from "./publicPayPortalPromo";
import QuickPayHeader from "./quickPayHeader";
import "../invoices/invoice-payment/paymentReturn.scss";
import "../shipments/labels-receipts/labelsReceipts.scss";
import "./publicPayment.scss";

const paymentService = PaymentService.getInstance();
const TERMINAL = new Set(["COMPLETED", "FAILED", "CANCELLED", "EXPIRED"]);
const MAX_POLLS = 20;

const STATUS_TITLE: Record<string, string> = {
  COMPLETED: "Payment successful",
  FAILED: "Payment not completed",
  CANCELLED: "Payment cancelled",
  EXPIRED: "Payment expired",
};

const methodLabel = (method?: string): string => {
  if (method === "CC") return "Credit Card";
  if (method === "CK" || method === "BANK" || method === "ECHECK") return "eCheck";
  return method || "";
};

const CopyButton: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <button
    type="button"
    className="xgs-public-payment__copy"
    aria-label={`Copy ${label}`}
    onClick={() => copyTextToClipboard(value)}
  >
    <XGSIcon icon={XGSIcons.faCopy} />
  </button>
);

const PublicPaymentReturnPage: React.FC = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const paymentId = params.get("paymentId");
  const accessToken = params.get("accessToken");
  const providerReturnStatus = params.get("status");
  const [status, setStatus] = useState<PaymentStatusResponse | null>(null);
  const [message, setMessage] = useState("Confirming your payment…");
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const polls = useRef(0);

  const runConfirm = useCallback(async () => {
    if (!paymentId || !accessToken) {
      setConfirmError("This payment confirmation link is incomplete.");
      return;
    }
    const response = await paymentService.confirmPublicPayment(
      paymentId,
      accessToken,
      providerReturnStatus
    );
    if (!response.ok() || !response.data) {
      const err = response.getError?.();
      setConfirmError(typeof err === "string" ? err : "Unable to confirm payment status.");
      return;
    }
    setStatus(response.data);
    if (TERMINAL.has(response.data.status)) {
      return;
    }
    setMessage("Still confirming with the payment provider…");
    if (polls.current < MAX_POLLS) {
      polls.current += 1;
      setTimeout(runConfirm, 3000);
    } else {
      setMessage("Payment is still processing. Refresh this page in a few minutes.");
    }
  }, [paymentId, accessToken, providerReturnStatus]);

  useEffect(() => {
    polls.current = 0;
    runConfirm();
  }, [runConfirm]);

  const onDownload = async () => {
    if (!paymentId || !accessToken || downloading) {
      return;
    }
    setPdfError(null);
    setDownloading(true);
    const response = await paymentService.getPublicPaymentReceiptPdf(paymentId, accessToken);
    setDownloading(false);
    if (!response.ok() || !response.data) {
      setPdfError("Receipt is not available yet.");
      return;
    }
    const blob = new Blob([response.data], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "payment-receipt.pdf";
    anchor.click();
    window.URL.revokeObjectURL(url);
  };

  const done = !!(status && TERMINAL.has(status.status));
  const succeeded = status?.status === "COMPLETED";
  const invoices = status?.invoices?.length
    ? status.invoices
    : (status?.invoiceNumbers || []).map((invoiceNumber) => ({
        invoiceNumber,
        amountCents: undefined as number | undefined,
      }));
  const summaryInvoices = invoices.some((line) => line.amountCents != null) ? invoices : undefined;
  const paidAt = status?.dateUpdated || status?.dateCreated;

  return (
    <div className="xgs-public-payment">
      <QuickPayHeader />
      <div className="xgs-public-payment__content">
        <div className="xgs-public-payment__layout">
          <div className="xgs-public-payment__main">
            <section className="xgs-print-download__card xgs-public-payment__result">
              {!done && !confirmError && paymentId && accessToken && (
                <div className="xgs-public-payment__pending">
                  <Loading isLoading />
                  <p>{message}</p>
                </div>
              )}
              {confirmError && (
                <div className="xgs-public-payment__result-head">
                  <span className="xgs-public-payment__result-mark xgs-public-payment__result-mark--quiet" aria-hidden>
                    <XGSIcon icon={XGSIcons.faTimes} />
                  </span>
                  <div>
                    <h2 className="xgs-public-payment__thanks">Payment not completed</h2>
                    <p className="xgs-public-payment__status">{confirmError}</p>
                  </div>
                </div>
              )}
              {done && status && (
                <>
                  <div className="xgs-public-payment__result-head">
                    <span
                      className={`xgs-public-payment__result-mark${
                        succeeded ? "" : " xgs-public-payment__result-mark--quiet"
                      }`}
                      aria-hidden
                    >
                      <XGSIcon icon={succeeded ? XGSIcons.faCheck : XGSIcons.faTimes} />
                    </span>
                    <div>
                      <h2 className="xgs-public-payment__thanks">
                        {STATUS_TITLE[status.status] || "Payment status"}
                      </h2>
                      <p className="xgs-public-payment__status">
                        {succeeded
                          ? "Thank you for your payment."
                          : "This payment was not completed."}
                      </p>
                    </div>
                  </div>
                  {succeeded && (
                    <button
                      type="button"
                      className="xgs-public-payment__receipt"
                      onClick={onDownload}
                      disabled={downloading}
                    >
                      <span className="xgs-public-payment__receipt-icon" aria-hidden>
                        <XGSIcon icon={XGSIcons.faFileAlt} />
                      </span>
                      <span className="xgs-public-payment__receipt-copy">
                        <span className="xgs-public-payment__receipt-title">
                          {downloading ? "Preparing receipt…" : "Download receipt"}
                        </span>
                        <span className="xgs-public-payment__receipt-hint">PDF for this payment</span>
                      </span>
                      <XGSIcon icon={XGSIcons.faDownload} />
                    </button>
                  )}
                  {pdfError && <p className="xgs-public-payment__field-error">{pdfError}</p>}
                  {succeeded && (
                    <a className="xgs-public-payment__again" href="/pay">
                      Make another payment
                    </a>
                  )}
                  <div className="payment-return">
                    <div className="payment-return__receipt payment-receipt">
                      {status.amountCents != null && (
                        <PaymentSummary
                          amountCents={status.amountCents}
                          surchargeAmountCents={status.surchargeAmountCents}
                          invoices={summaryInvoices}
                        />
                      )}
                      <div className="payment-receipt__breakdown">
                        <div className="payment-receipt__breakdown-title">PAYMENT DETAILS</div>
                        {paidAt && (
                          <div className="payment-receipt__breakdown-row">
                            <span>Date paid</span>
                            <span>
                              <Moment utc local format="MM/DD/YYYY h:mm A">
                                {paidAt}
                              </Moment>
                            </span>
                          </div>
                        )}
                        {status.paidBy && (
                          <div className="payment-receipt__breakdown-row">
                            <span>Paid by</span>
                            <span>{status.paidBy}</span>
                          </div>
                        )}
                        {methodLabel(status.paymentMethod) && (
                          <div className="payment-receipt__breakdown-row">
                            <span>Payment method</span>
                            <span>{methodLabel(status.paymentMethod)}</span>
                          </div>
                        )}
                        <div className="payment-receipt__breakdown-row">
                          <span>Payment ID</span>
                          <span className="xgs-public-payment__copy-line">
                            {status.paymentId}
                            <CopyButton label="payment ID" value={status.paymentId} />
                          </span>
                        </div>
                      </div>
                      {invoices.length > 0 && (
                        <div className="payment-receipt__breakdown">
                          <div className="payment-receipt__breakdown-title">INVOICES</div>
                          <div className="payment-receipt__breakdown-row payment-receipt__breakdown-head">
                            <span>XGS probill number</span>
                            <span>Amount paid</span>
                          </div>
                          {invoices.map((line) => (
                            <div className="payment-receipt__breakdown-row" key={line.invoiceNumber}>
                              <span className="xgs-public-payment__copy-line">
                                {formatProbillDisplay(line.invoiceNumber)}
                                <CopyButton
                                  label="XGS probill number"
                                  value={String(line.invoiceNumber)}
                                />
                              </span>
                              <span>
                                {line.amountCents != null ? (
                                  <Amount amount={line.amountCents / 100} />
                                ) : (
                                  "—"
                                )}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  {done && (
                    <div className="xgs-public-payment__next">
                      {succeeded ? (
                        <>
                          <p>
                            Looking for invoice history, receipts, and shipment visibility? Sign in to
                            the Customer Portal or create an account.
                          </p>
                          <p className="xgs-public-payment__next-links">
                            <a href="/">Sign in</a>
                            <a href={Routes.portalRegistration}>Create account</a>
                          </p>
                        </>
                      ) : (
                        <p className="xgs-public-payment__next-links">
                          <a href="/pay">Return to Quick Pay</a>
                        </p>
                      )}
                    </div>
                  )}
                </>
              )}
              {!status && (confirmError || !paymentId) && (
                <p className="xgs-public-payment__next-links">
                  <a href="/pay">Return to Quick Pay</a>
                </p>
              )}
            </section>
          </div>
          <PublicPayPortalPromo />
        </div>
      </div>
    </div>
  );
};

export default PublicPaymentReturnPage;
